import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles,
  Crown,
  Award,
  DollarSign,
  Gem,
  BadgeDollarSign,
  Flame,
  Camera,
  Music,
  Users,
  Palette,
  Disc3,
  Globe,
  Radio,
  Headphones,
  PartyPopper,
  Heart,
  Wine,
  HelpCircle,
  ChevronLeft,
  ChevronRight,
  Check,
  MapPin,
  Calendar,
  Shirt,
  UserPlus,
  Hash,
  Mail,
  Copy,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import { Button, Card } from '../../components/ui';
import type {
  ExperienceLevel,
  BudgetRange,
  VibeTag,
  MusicTag,
  CrowdPreference,
  AgeRange,
  CostumePreference,
  Location,
  TrinidadArea,
} from '../../types/database';
import { LOCATIONS, PAST_BANDS, TRINIDAD_AREAS } from '../../types/database';
import styles from './Onboarding.module.css';

type StepType = 'welcome' | 'options' | 'form' | 'multi-select' | 'friends';

interface QuizOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
}

interface Step {
  id: string;
  type: StepType;
  title: string;
  subtitle?: string;
  options?: QuizOption[];
  multiSelect?: boolean;
  maxSelections?: number;
}

const steps: Step[] = [
  {
    id: 'welcome',
    type: 'welcome',
    title: "Let's find your perfect band",
    subtitle: "We'll ask you a few questions to match you with the right Carnival experience. Takes about 2 minutes.",
  },
  {
    id: 'basic_info',
    type: 'form',
    title: 'Tell us about yourself',
    subtitle: 'This helps us personalize your matches',
  },
  {
    id: 'experience',
    type: 'options',
    title: 'How many times have you played mas?',
    options: [
      { value: 'first_time', label: "First time (I'm a fresh mas baby)", icon: <Sparkles size={24} /> },
      { value: 'some', label: '2-5 times (I know the vibes)', icon: <Award size={24} /> },
      { value: 'veteran', label: '6+ times (Veteran masquerader)', icon: <Crown size={24} /> },
    ],
  },
  {
    id: 'past_bands',
    type: 'multi-select',
    title: 'Which bands have you played with?',
    subtitle: 'Select all that apply',
    multiSelect: true,
    options: PAST_BANDS.map((band) => ({ value: band, label: band })),
  },
  {
    id: 'budget',
    type: 'options',
    title: "What's your costume budget?",
    subtitle: 'Prices in USD',
    options: [
      { value: 'under_500', label: 'Under $500', icon: <DollarSign size={24} /> },
      { value: '500_800', label: '$500 - $800', icon: <BadgeDollarSign size={24} /> },
      { value: '800_1200', label: '$800 - $1200', icon: <Gem size={24} /> },
      { value: '1200_plus', label: '$1200+', icon: <Gem size={24} /> },
      { value: 'unlimited', label: 'Money eh no problem', icon: <Crown size={24} /> },
    ],
  },
  {
    id: 'costume_preference',
    type: 'options',
    title: 'What type of costume are you looking for?',
    options: [
      { value: 'frontline', label: 'Frontline (most elaborate, highest price)', icon: <Crown size={24} /> },
      { value: 'midline', label: 'Midline (balanced quality and price)', icon: <Gem size={24} /> },
      { value: 'backline', label: 'Backline (simpler, lower price)', icon: <Shirt size={24} /> },
      { value: 'no_preference', label: 'No preference', icon: <HelpCircle size={24} /> },
    ],
  },
  {
    id: 'vibes',
    type: 'options',
    title: 'What kind of experience do you want?',
    subtitle: 'Pick up to 2',
    multiSelect: true,
    maxSelections: 2,
    options: [
      { value: 'bacchanal', label: 'Bacchanal (wine down, get on bad)', icon: <Flame size={24} /> },
      { value: 'pretty', label: 'Pretty mas (I came for the photos)', icon: <Camera size={24} /> },
      { value: 'music', label: "Music-focused (following the truck)", icon: <Music size={24} /> },
      { value: 'social', label: 'Social vibes (liming with my crew)', icon: <Users size={24} /> },
      { value: 'cultural', label: 'Cultural (traditional mas elements)', icon: <Palette size={24} /> },
    ],
  },
  {
    id: 'music',
    type: 'options',
    title: 'What music makes you chip?',
    subtitle: 'Pick up to 2',
    multiSelect: true,
    maxSelections: 2,
    options: [
      { value: 'soca', label: 'Soca (power or groovy)', icon: <Disc3 size={24} /> },
      { value: 'dancehall', label: 'Dancehall (bashment)', icon: <Radio size={24} /> },
      { value: 'afrobeats', label: 'Afrobeats', icon: <Globe size={24} /> },
      { value: 'bouyon', label: 'Bouyon/regional', icon: <Music size={24} /> },
      { value: 'everything', label: 'Everything works', icon: <Headphones size={24} /> },
    ],
  },
  {
    id: 'crowd',
    type: 'options',
    title: "What's your ideal crowd?",
    options: [
      { value: 'young', label: 'Young and wild (18-30)', icon: <PartyPopper size={24} /> },
      { value: 'mixed', label: 'Mixed ages (everybody welcome)', icon: <Heart size={24} /> },
      { value: 'mature', label: 'Mature vibes (35+)', icon: <Wine size={24} /> },
      { value: 'any', label: "Doesn't matter to me", icon: <HelpCircle size={24} /> },
    ],
  },
  {
    id: 'friends',
    type: 'friends',
    title: 'Add your friends',
    subtitle: 'See which bands your friends matched with',
  },
];

export function Onboarding() {
  const navigate = useNavigate();
  const { user, profile, refreshProfile } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string | string[]>>({
    display_name: '',
    location: '',
    trinidad_area: '',
    age_range: '',
  });
  const [saving, setSaving] = useState(false);

  // Friends step state
  const [friendInput, setFriendInput] = useState('');
  const [friendInputType, setFriendInputType] = useState<'code' | 'email'>('code');
  const [friendStatus, setFriendStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [friendMessage, setFriendMessage] = useState('');
  const [addedFriends, setAddedFriends] = useState<{ id: string; name: string }[]>([]);
  const [codeCopied, setCodeCopied] = useState(false);

  const step = steps[currentStep];
  const totalSteps = steps.length;
  const progress = ((currentStep + 1) / totalSteps) * 100;

  const handleSelect = (value: string) => {
    if (step.multiSelect) {
      const current = (answers[step.id] as string[]) || [];
      if (current.includes(value)) {
        setAnswers({
          ...answers,
          [step.id]: current.filter((v) => v !== value),
        });
      } else if (!step.maxSelections || current.length < step.maxSelections) {
        setAnswers({
          ...answers,
          [step.id]: [...current, value],
        });
      }
    } else {
      setAnswers({
        ...answers,
        [step.id]: value,
      });
    }
  };

  const isSelected = (value: string) => {
    const answer = answers[step.id];
    if (Array.isArray(answer)) {
      return answer.includes(value);
    }
    return answer === value;
  };

  const canProceed = () => {
    if (step.type === 'welcome') return true;
    if (step.type === 'friends') return true; // Can skip friends

    if (step.type === 'form') {
      return (
        (answers.display_name as string)?.trim() !== '' &&
        (answers.location as string) !== '' &&
        (answers.trinidad_area as string) !== '' &&
        (answers.age_range as string) !== ''
      );
    }

    const answer = answers[step.id];
    if (step.multiSelect) {
      // past_bands can be empty (first timer)
      if (step.id === 'past_bands') return true;
      return Array.isArray(answer) && answer.length > 0;
    }
    return !!answer;
  };

  const handleAddFriend = async () => {
    if (!friendInput.trim()) return;

    setFriendStatus('loading');

    let friendUser: { id: string; display_name: string | null } | null = null;

    if (friendInputType === 'code') {
      const { data, error } = await supabase
        .from('users')
        .select('id, display_name')
        .eq('app_code', friendInput.toUpperCase().trim())
        .single();

      if (error || !data) {
        setFriendStatus('error');
        setFriendMessage('No user found with that code');
        setTimeout(() => { setFriendStatus('idle'); setFriendMessage(''); }, 3000);
        return;
      }
      friendUser = data;
    } else {
      const email = friendInput.toLowerCase().trim();
      const { data, error } = await supabase
        .from('users')
        .select('id, display_name')
        .eq('email', email)
        .single();

      if (error || !data) {
        setFriendStatus('error');
        setFriendMessage('No user found with that email');
        setTimeout(() => { setFriendStatus('idle'); setFriendMessage(''); }, 3000);
        return;
      }
      friendUser = data;
    }

    if (friendUser.id === user?.id) {
      setFriendStatus('error');
      setFriendMessage("That's you!");
      setTimeout(() => { setFriendStatus('idle'); setFriendMessage(''); }, 3000);
      return;
    }

    if (addedFriends.find((f) => f.id === friendUser!.id)) {
      setFriendStatus('error');
      setFriendMessage('Already added!');
      setTimeout(() => { setFriendStatus('idle'); setFriendMessage(''); }, 3000);
      return;
    }

    const { data: existing } = await supabase
      .from('friendships')
      .select('id')
      .eq('user_id', user!.id)
      .eq('friend_id', friendUser.id)
      .single();

    if (existing) {
      setFriendStatus('error');
      setFriendMessage('Already friends!');
      setTimeout(() => { setFriendStatus('idle'); setFriendMessage(''); }, 3000);
      return;
    }

    const { error: friendshipError } = await supabase.from('friendships').insert({
      user_id: user!.id,
      friend_id: friendUser.id,
    });

    if (friendshipError) {
      setFriendStatus('error');
      setFriendMessage('Could not add friend');
      setTimeout(() => { setFriendStatus('idle'); setFriendMessage(''); }, 3000);
      return;
    }

    setFriendStatus('success');
    const friendName = friendUser.display_name || 'Friend';
    setFriendMessage(`Added ${friendName}!`);
    setAddedFriends([...addedFriends, { id: friendUser.id, name: friendName }]);
    setFriendInput('');

    setTimeout(() => { setFriendStatus('idle'); setFriendMessage(''); }, 3000);
  };

  const copyAppCode = async () => {
    if (profile?.app_code) {
      await navigator.clipboard.writeText(profile.app_code);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    }
  };

  const handleNext = async () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Save all answers to database
      setSaving(true);

      const pastBands = answers.past_bands as string[] || [];
      const filteredPastBands = pastBands.filter((b) => b !== 'None - First time');

      const updateData: Record<string, unknown> = {
        display_name: (answers.display_name as string).trim(),
        location: answers.location as Location,
        trinidad_area: answers.trinidad_area as TrinidadArea,
        age_range: answers.age_range as AgeRange,
        experience_level: answers.experience as ExperienceLevel,
        past_bands: filteredPastBands,
        budget_range: answers.budget as BudgetRange,
        costume_preference: answers.costume_preference as CostumePreference,
        vibe_preferences: answers.vibes as VibeTag[],
        music_preferences: answers.music as MusicTag[],
        crowd_preference: answers.crowd as CrowdPreference,
        onboarding_completed: true,
      };

      const { error } = await supabase
        .from('users')
        .update(updateData)
        .eq('id', user!.id);

      if (error) {
        console.error('Error saving preferences:', error);
        setSaving(false);
        return;
      }

      await refreshProfile();
      navigate('/discover');
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Render welcome step
  if (step.type === 'welcome') {
    return (
      <div className={styles.container}>
        <div className={styles.progressBar}>
          <div className={styles.progressFill} style={{ width: `${progress}%` }} />
        </div>

        <div className={styles.welcomeContent}>
          <div className={styles.welcomeIcon}>
            <Sparkles size={48} />
          </div>
          <h1 className={styles.welcomeTitle}>{step.title}</h1>
          <p className={styles.welcomeSubtitle}>{step.subtitle}</p>

          <div className={styles.welcomeFeatures}>
            <div className={styles.welcomeFeature}>
              <MapPin size={20} />
              <span>Tell us where you're from</span>
            </div>
            <div className={styles.welcomeFeature}>
              <DollarSign size={20} />
              <span>Set your budget</span>
            </div>
            <div className={styles.welcomeFeature}>
              <Music size={20} />
              <span>Pick your vibes</span>
            </div>
            <div className={styles.welcomeFeature}>
              <Users size={20} />
              <span>Find your friends</span>
            </div>
          </div>
        </div>

        <div className={styles.actions}>
          <Button onClick={handleNext} fullWidth>
            Get Started
            <ChevronRight size={18} />
          </Button>
        </div>
      </div>
    );
  }

  // Render form step (basic info)
  if (step.type === 'form') {
    return (
      <div className={styles.container}>
        <div className={styles.progressBar}>
          <div className={styles.progressFill} style={{ width: `${progress}%` }} />
        </div>

        <div className={styles.header}>
          <span className={styles.step}>
            {currentStep + 1} of {totalSteps}
          </span>
          <h1 className={styles.title}>{step.title}</h1>
          {step.subtitle && <p className={styles.subtitle}>{step.subtitle}</p>}
        </div>

        <div className={styles.formFields}>
          <div className={styles.formField}>
            <label className={styles.label}>
              <Users size={16} />
              Display Name
            </label>
            <input
              type="text"
              className={styles.input}
              placeholder="What should we call you?"
              value={answers.display_name as string}
              onChange={(e) => setAnswers({ ...answers, display_name: e.target.value })}
            />
          </div>

          <div className={styles.formField}>
            <label className={styles.label}>
              <MapPin size={16} />
              Where do you live?
            </label>
            <select
              className={styles.select}
              value={answers.location as string}
              onChange={(e) => setAnswers({ ...answers, location: e.target.value })}
            >
              <option value="">Select your location</option>
              {LOCATIONS.map((loc) => (
                <option key={loc} value={loc}>
                  {loc}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.formField}>
            <label className={styles.label}>
              <MapPin size={16} />
              Where will you be coming from on Carnival day?
            </label>
            <select
              className={styles.select}
              value={answers.trinidad_area as string}
              onChange={(e) => setAnswers({ ...answers, trinidad_area: e.target.value })}
            >
              <option value="">Select area in Trinidad</option>
              {TRINIDAD_AREAS.map((area) => (
                <option key={area} value={area}>
                  {area}
                </option>
              ))}
            </select>
            <p className={styles.fieldHint}>Helps bands plan buses and pickup points</p>
          </div>

          <div className={styles.formField}>
            <label className={styles.label}>
              <Calendar size={16} />
              Age Range
            </label>
            <div className={styles.ageOptions}>
              {[
                { value: '18_24', label: '18-24' },
                { value: '25_34', label: '25-34' },
                { value: '35_44', label: '35-44' },
                { value: '45_plus', label: '45+' },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`${styles.ageOption} ${answers.age_range === opt.value ? styles.selected : ''}`}
                  onClick={() => setAnswers({ ...answers, age_range: opt.value })}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className={styles.actions}>
          {currentStep > 0 && (
            <Button variant="ghost" onClick={handleBack}>
              <ChevronLeft size={18} />
              Back
            </Button>
          )}
          <Button onClick={handleNext} disabled={!canProceed()}>
            Continue
            <ChevronRight size={18} />
          </Button>
        </div>
      </div>
    );
  }

  // Render friends step
  if (step.type === 'friends') {
    return (
      <div className={styles.container}>
        <div className={styles.progressBar}>
          <div className={styles.progressFill} style={{ width: `${progress}%` }} />
        </div>

        <div className={styles.header}>
          <span className={styles.step}>
            {currentStep + 1} of {totalSteps}
          </span>
          <h1 className={styles.title}>{step.title}</h1>
          {step.subtitle && <p className={styles.subtitle}>{step.subtitle}</p>}
        </div>

        <div className={styles.friendsContent}>
          {/* Your code */}
          <div className={styles.yourCodeSection}>
            <p className={styles.yourCodeLabel}>Your friend code</p>
            <div className={styles.yourCode}>
              <span className={styles.codeText}>{profile?.app_code || '------'}</span>
              <button className={styles.copyBtn} onClick={copyAppCode}>
                {codeCopied ? <CheckCircle size={18} /> : <Copy size={18} />}
              </button>
            </div>
            <p className={styles.yourCodeHint}>Share this code with friends so they can add you</p>
          </div>

          {/* Add friend */}
          <div className={styles.addFriendSection}>
            <div className={styles.inputTypeToggle}>
              <button
                className={`${styles.toggleBtn} ${friendInputType === 'code' ? styles.active : ''}`}
                onClick={() => setFriendInputType('code')}
              >
                <Hash size={16} />
                Friend Code
              </button>
              <button
                className={`${styles.toggleBtn} ${friendInputType === 'email' ? styles.active : ''}`}
                onClick={() => setFriendInputType('email')}
              >
                <Mail size={16} />
                Email
              </button>
            </div>

            <div className={styles.addFriendInput}>
              <input
                type={friendInputType === 'email' ? 'email' : 'text'}
                className={styles.input}
                placeholder={friendInputType === 'code' ? "Enter friend's code" : "Enter friend's email"}
                value={friendInput}
                onChange={(e) => setFriendInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddFriend()}
              />
              <button
                className={styles.addBtn}
                onClick={handleAddFriend}
                disabled={!friendInput.trim() || friendStatus === 'loading'}
              >
                <UserPlus size={18} />
              </button>
            </div>

            {friendMessage && (
              <div className={`${styles.friendMessage} ${styles[friendStatus]}`}>
                {friendStatus === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
                {friendMessage}
              </div>
            )}

            <p className={styles.friendNote}>
              Friends must already have a Mas Match account to be added.
            </p>
          </div>

          {/* Added friends */}
          {addedFriends.length > 0 && (
            <div className={styles.addedFriends}>
              <p className={styles.addedLabel}>Added ({addedFriends.length}):</p>
              <div className={styles.addedList}>
                {addedFriends.map((friend) => (
                  <span key={friend.id} className={styles.addedFriend}>
                    <Check size={12} />
                    {friend.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className={styles.actions}>
          <Button variant="ghost" onClick={handleBack}>
            <ChevronLeft size={18} />
            Back
          </Button>
          <Button onClick={handleNext} loading={saving}>
            {addedFriends.length > 0 ? "Let's Match!" : 'Skip for now'}
          </Button>
        </div>
      </div>
    );
  }

  // Render multi-select step (past bands)
  if (step.type === 'multi-select') {
    const selectedBands = (answers[step.id] as string[]) || [];
    return (
      <div className={styles.container}>
        <div className={styles.progressBar}>
          <div className={styles.progressFill} style={{ width: `${progress}%` }} />
        </div>

        <div className={styles.header}>
          <span className={styles.step}>
            {currentStep + 1} of {totalSteps}
          </span>
          <h1 className={styles.title}>{step.title}</h1>
          {step.subtitle && <p className={styles.subtitle}>{step.subtitle}</p>}
        </div>

        <div className={styles.multiSelectGrid}>
          {step.options?.map((option) => (
            <button
              key={option.value}
              className={`${styles.multiSelectOption} ${isSelected(option.value) ? styles.selected : ''}`}
              onClick={() => handleSelect(option.value)}
            >
              {option.label}
              {isSelected(option.value) && <Check size={14} />}
            </button>
          ))}
        </div>

        {selectedBands.length > 0 && (
          <p className={styles.selectedCount}>
            {selectedBands.length} selected
          </p>
        )}

        <div className={styles.actions}>
          <Button variant="ghost" onClick={handleBack}>
            <ChevronLeft size={18} />
            Back
          </Button>
          <Button onClick={handleNext} disabled={!canProceed()}>
            Continue
            <ChevronRight size={18} />
          </Button>
        </div>
      </div>
    );
  }

  // Render options step (default)
  return (
    <div className={styles.container}>
      <div className={styles.progressBar}>
        <div className={styles.progressFill} style={{ width: `${progress}%` }} />
      </div>

      <div className={styles.header}>
        <span className={styles.step}>
          {currentStep + 1} of {totalSteps}
        </span>
        <h1 className={styles.title}>{step.title}</h1>
        {step.subtitle && <p className={styles.subtitle}>{step.subtitle}</p>}
      </div>

      <div className={styles.options}>
        {step.options?.map((option) => (
          <Card
            key={option.value}
            className={`${styles.option} ${isSelected(option.value) ? styles.selected : ''}`}
            onClick={() => handleSelect(option.value)}
          >
            {option.icon && <span className={styles.optionIcon}>{option.icon}</span>}
            <span className={styles.optionLabel}>{option.label}</span>
            {isSelected(option.value) && <Check size={20} className={styles.checkIcon} />}
          </Card>
        ))}
      </div>

      <div className={styles.actions}>
        {currentStep > 0 && (
          <Button variant="ghost" onClick={handleBack}>
            <ChevronLeft size={18} />
            Back
          </Button>
        )}
        <Button
          onClick={handleNext}
          disabled={!canProceed()}
          loading={saving}
          fullWidth={currentStep === 0}
        >
          {currentStep === totalSteps - 1 ? "Let's Match!" : 'Continue'}
          {currentStep < totalSteps - 1 && <ChevronRight size={18} />}
        </Button>
      </div>
    </div>
  );
}
